/**
 * Goals (D244): milestones met once a year, each paying a small multiplier for the rest
 * of the year. A goal is checked whenever the player is here (every check-in and action), not
 * inside `advanceIdle`, so the closed-form advance stays exact and the reward lands when the
 * player can see it.
 *
 * D334: goals grow with the world. A goal but a chain has tiers, each met on its own and kept
 * under its own key (`id` for the first, `id@2` on), so a save from before tiers keeps every
 * goal it met as the first tier. Targets rise with the festivals held this generation, and a
 * later generation counts as more festivals. Long goals (`content.longGoals`) are met once a
 * generation, kept through its festivals, and reset when the world is passed on.
 */
import type { IdleContent, IdleEffectSpecT, IdleGoalT } from '@hedgerow/data';
import type { IdleWorldState } from './state.ts';

/**
 * D335: the goal rules now in force. Raise it with any change that can meet goal tiers a world has
 * already reached (new tiers, lower targets, new goals): a save stamped lower has that backlog met
 * quietly on load (`idleSettleGoals`), never shown as news.
 * 1: D334's tiers, scaled targets and long goals.
 */
export const IDLE_GOAL_RULES = 1;

const LONG_KINDS: ReadonlySet<IdleGoalT['kind']> = new Set(['festivals', 'perks', 'lifetime']);

/** True for a long goal (D334): one met once a generation, not once a year. */
export function idleGoalIsLong(goal: IdleGoalT): boolean {
  return LONG_KINDS.has(goal.kind);
}

/** The key a goal's tier is met under: `id` for the first tier, `id@2` on (D334). */
export function idleGoalKey(id: string, tier: number): string {
  return tier <= 1 ? id : `${id}@${tier}`;
}

/** A met key as its goal id and tier. */
export function idleGoalParse(key: string): { id: string; tier: number } {
  const at = key.indexOf('@');
  if (at < 0) return { id: key, tier: 1 };
  return { id: key.slice(0, at), tier: Number(key.slice(at + 1)) || 1 };
}

function generation(state: IdleWorldState): number {
  return state.permanent.lineage?.generation ?? 1;
}

function festivalsThisGeneration(state: IdleWorldState): number {
  return state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
}

/**
 * D334: how far along the world is, which the year's goal targets scale with: the festivals held
 * this generation, and `goalScale.perGeneration` more for each generation before this one.
 */
export function idleGoalLevel(state: IdleWorldState, content: IdleContent): number {
  const sc = content.goalScale;
  if (!sc) return 0;
  return festivalsThisGeneration(state) + sc.perGeneration * (generation(state) - 1);
}

/**
 * How many tiers a goal has now: a chain or a goal with no scale has one. A year's goal gains a
 * tier for each `goalScale.festivalsPerTier` festivals held (the generation counted as for
 * targets), up to `goalScale.tiers`, so the first year is played as it always was.
 */
export function idleGoalTiers(
  state: IdleWorldState,
  content: IdleContent,
  goal: IdleGoalT,
): number {
  if (goal.targets) return goal.targets.length;
  const sc = content.goalScale;
  if (goal.kind === 'chain' || idleGoalIsLong(goal) || !sc) return 1;
  const opened = 1 + Math.floor(idleGoalLevel(state, content) / sc.festivalsPerTier);
  return Math.min(sc.tiers, opened);
}

const FRIENDLY = [10, 25, 50, 75, 100, 150, 200, 250, 300, 400, 500];

/** D372: the friendly numbers in order: the list, then steps of 100 to 1,000, then of 250. */
function friendlyStep(n: number): number {
  if (n < 10) return n + 1;
  const next = FRIENDLY.find((f) => f > n);
  if (next !== undefined) return next;
  return n < 1000 ? Math.floor(n / 100) * 100 + 100 : Math.floor(n / 250) * 250 + 250;
}

/** D372: the largest friendly number not above `n`. Below 10 it is a whole number. */
export function idleFriendly(n: number): number {
  if (n < 10) return Math.max(1, Math.round(n));
  if (n >= 1000) return Math.floor(n / 250) * 250;
  if (n >= 500) return Math.floor(n / 100) * 100;
  return FRIENDLY.filter((f) => f <= n).pop()!;
}

/**
 * D372: scaled targets made friendly, never below the unscaled `base`, each tier above the last.
 * `raw(t)` is tier t's scaled target.
 */
function friendlyTarget(raw: (t: number) => number, base: number, tier: number): number {
  let target = 0;
  for (let t = 1; t <= tier; t++) {
    const f = Math.max(base, idleFriendly(raw(t)));
    target = t === 1 || f > target ? f : friendlyStep(target);
  }
  return target;
}

/** The target of one tier of a goal (1 for a chain): units, coins, hands, festivals or perk levels. */
export function idleGoalTarget(
  state: IdleWorldState,
  content: IdleContent,
  goal: IdleGoalT,
  tier = 1,
): number {
  if (goal.targets) return goal.targets[Math.min(goal.targets.length, tier) - 1]!;
  const sc = content.goalScale;
  const level = idleGoalLevel(state, content);
  // D372: a year begun before friendly targets keeps its own until the next festival.
  const friendly = (raw: (t: number) => number, base: number): number =>
    state.year.friendlyGoals ? friendlyTarget(raw, base, tier) : raw(tier);
  switch (goal.kind) {
    case 'own':
    case 'hands': {
      const base = goal.count ?? 1;
      if (!sc) return base;
      const k = sc[goal.kind];
      return friendly(
        (t) => Math.round((base + k.perFestival * level) * k.tierStep ** (t - 1)),
        base,
      );
    }
    case 'earn': {
      const base = goal.coins ?? 1;
      if (!sc) return base;
      return friendly(
        (t) => base * sc.earn.perFestival ** level * sc.earn.tierStep ** (t - 1),
        base,
      );
    }
    case 'chain':
      return 1;
    case 'festivals':
    case 'perks':
      return goal.count ?? 1;
    case 'lifetime':
      return goal.coins ?? 1;
  }
}

/** What the world has towards a goal, in the units of its target. */
function idleGoalHave(state: IdleWorldState, goal: IdleGoalT): number {
  const y = state.year;
  switch (goal.kind) {
    case 'own':
      return y.producers[goal.producer ?? '']?.owned ?? 0;
    case 'earn':
      return y.earned;
    case 'chain':
      return goal.chain !== undefined && y.chains.includes(goal.chain) ? 1 : 0;
    case 'hands':
      return Object.values(y.producers).filter((p) => p.hand).length;
    case 'festivals':
      return festivalsThisGeneration(state);
    case 'perks':
      return Object.values(state.permanent.perks).reduce((a, n) => a + n, 0);
    case 'lifetime':
      return state.permanent.lifetimeEarned - (state.permanent.lineage?.lifetimeBefore ?? 0);
  }
}

/** How far one tier of a goal has got, from 0 to 1. */
export function idleGoalProgress(
  state: IdleWorldState,
  content: IdleContent,
  goal: IdleGoalT,
  tier = 1,
): number {
  const need = idleGoalTarget(state, content, goal, tier);
  return need > 0 ? Math.min(1, idleGoalHave(state, goal) / need) : 0;
}

/** Long goals met in this generation, by key. */
export function idleLongGoalsMet(state: IdleWorldState): string[] {
  const rec = state.permanent.longGoals;
  return rec && rec.generation === generation(state) ? rec.met : [];
}

function metKeys(state: IdleWorldState, goal: IdleGoalT): string[] {
  return idleGoalIsLong(goal) ? idleLongGoalsMet(state) : state.year.goals;
}

/** D334: a tier's reward: the goal's for the first, `laterTierReward` of it for each after. */
export function idleGoalReward(
  content: IdleContent,
  goal: IdleGoalT,
  tier: number,
): IdleEffectSpecT {
  const share = content.goalScale?.laterTierReward ?? 1;
  if (tier <= 1 || share === 1) return goal.reward;
  return { ...goal.reward, factor: 1 + (goal.reward.factor - 1) * share };
}

/** A tier's Heritage: the goal's for the first, `laterTierReward` of it (rounded down) after. */
function tierHeritage(content: IdleContent, goal: IdleGoalT, tier: number): number {
  const h = goal.heritage ?? 0;
  if (tier <= 1) return h;
  return Math.floor(h * (content.goalScale?.laterTierReward ?? 1));
}

export interface IdleGoalView {
  goal: IdleGoalT;
  /** D334: met once a generation and kept through its festivals. */
  long: boolean;
  /** Every tier is met. */
  met: boolean;
  /** The tier on show, 1-based: the first not yet met, or the last once all are. */
  tier: number;
  tiers: number;
  /** Tiers met. */
  metTiers: number;
  /** The tier on show's target. */
  target: number;
  /** The tier on show's progress, 0 to 1. */
  progress: number;
}

/** Every goal, the year's then the long ones, in content order, each at its first tier not met. */
export function idleGoals(state: IdleWorldState, content: IdleContent): IdleGoalView[] {
  return [...content.goals, ...content.longGoals].map((goal) => {
    const keys = metKeys(state, goal);
    const tiers = idleGoalTiers(state, content, goal);
    let metTiers = 0;
    let first = 0;
    for (let k = 1; k <= tiers; k++)
      if (keys.includes(idleGoalKey(goal.id, k))) metTiers++;
      else if (first === 0) first = k;
    const met = first === 0;
    const tier = met ? tiers : first;
    return {
      goal,
      long: idleGoalIsLong(goal),
      met,
      tier,
      tiers,
      metTiers,
      target: idleGoalTarget(state, content, goal, tier),
      progress: met ? 1 : idleGoalProgress(state, content, goal, tier),
    };
  });
}

/** D334: tiers met and tiers in all, the year's goals and the long ones together. */
export function idleGoalTally(
  state: IdleWorldState,
  content: IdleContent,
): { met: number; total: number } {
  let met = 0;
  let total = 0;
  for (const g of idleGoals(state, content)) {
    met += g.metTiers;
    total += g.tiers;
  }
  return { met, total };
}

/**
 * Keys of the goal tiers met in `after` that were not met in `before`, for the screen to celebrate.
 * D335: none when the two were settled under different goal rules: the difference is then a
 * rules change met quietly on load, not news.
 */
export function idleGoalsNewlyMet(before: IdleWorldState, after: IdleWorldState): string[] {
  if (before.permanent.goalRules !== after.permanent.goalRules) return [];
  const was = new Set([...before.year.goals, ...idleLongGoalsMet(before)]);
  return [...after.year.goals, ...idleLongGoalsMet(after)].filter((k) => !was.has(k));
}

/** Mark every goal tier now met. Returns the same state when none is new. */
export function meetIdleGoals(state: IdleWorldState, content: IdleContent): IdleWorldState {
  const freshYear: string[] = [];
  const freshLong: string[] = [];
  let heritage = 0;
  const longMet = idleLongGoalsMet(state);
  for (const goal of [...content.goals, ...content.longGoals]) {
    const long = idleGoalIsLong(goal);
    const keys = long ? longMet : state.year.goals;
    const tiers = idleGoalTiers(state, content, goal);
    for (let k = 1; k <= tiers; k++) {
      const key = idleGoalKey(goal.id, k);
      if (keys.includes(key) || idleGoalProgress(state, content, goal, k) < 1) continue;
      (long ? freshLong : freshYear).push(key);
      heritage += tierHeritage(content, goal, k);
    }
  }
  if (freshYear.length === 0 && freshLong.length === 0) return state;
  // A goal also earns a little Heritage (D252), banked in the year and paid by the festival, so
  // it never lifts income in the middle of a year.
  const year = freshYear.length
    ? { ...state.year, goals: [...state.year.goals, ...freshYear] }
    : { ...state.year };
  if (heritage > 0) year.heritage = (state.year.heritage ?? 0) + heritage;
  const permanent = freshLong.length
    ? {
        ...state.permanent,
        longGoals: { generation: generation(state), met: [...longMet, ...freshLong] },
      }
    : state.permanent;
  return { ...state, year, permanent };
}

/**
 * D335: a stored world brought under the goal rules now in force. A world stamped with older rules
 * (or none) has every goal tier it had already reached met now, quietly, with the Heritage those
 * tiers pay, and is stamped; a world already stamped is returned as it is. A world waiting on its
 * new year is only stamped: its year has been paid for, and the next year starts its goals afresh.
 */
export function idleSettleGoals(state: IdleWorldState, content: IdleContent): IdleWorldState {
  if (state.permanent.goalRules === IDLE_GOAL_RULES) return state;
  const met = state.festivalPending ? state : meetIdleGoals(state, content);
  return { ...met, permanent: { ...met.permanent, goalRules: IDLE_GOAL_RULES } };
}
